import React, { useEffect, useState } from 'react';
import { TargetFactory } from '../../utils';
import { TARGET_TYPES } from '../../models/target-type';
import ActivateTargetAPI from '../../ActivateTargetAPI';
import { Target } from '../../models/target';
import HdDocLink from '../../../../docs/components/doc-link/HdDocLink';
import TargetSettingsFormWrapper from '../TargetSettingsFormWrapper/TargetSettingsFormWrapper';
import HdIcon from '../../../../media/components/icon/HdIcon';
import useService from '../../../../injector/hooks/useService';
import { TargetEditService } from '../../services/target-edit.service';
import { TargetTypeInfo } from '../../models/target-type-info';
import RetryApiAlert from '../../../../fallback-screens/components/retry-api-alert/RetryApiAlert';
import { HdButton } from '../../../../components';
import styles from './styles.module.scss';
import { NodeConfigData } from '../../../../connector/connector-form/interfaces/nodeFormData';
import { TargetSettingsService } from '../../services/target-settings.service';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';
import { ConnectorFormShimmer } from '../../../../connector/connector-form/components/connector-form-shimmer/ConnectorFormShimmer';
import FullScreenModal from '../../../../modal/components/full-screen-modal/FullScreenModal';
import { useEditEntityDialog } from '../../../../core/hooks/useEditEntityDialog';
import { AngularDiProviders } from '../../../../injector/components/angular-di/AngularDiProviders';

export const EDIT_TARGET_QUERY_PARAM_TRIGGER = 'edit_target';

const injectorWithTargetSettingsService = [TargetSettingsService];

export function EditTargetDialog() {
  const { editModalConfig: editTargetModalConfig, navigate } = useEditEntityDialog({
    editEntityQueryParam: EDIT_TARGET_QUERY_PARAM_TRIGGER,
    handleDialog: true
  });

  return (
    <FullScreenModal open={editTargetModalConfig.visible}>
      <AngularDiProviders providers={injectorWithTargetSettingsService}>
        <EditTargetDialogInner
          onClose={() => navigate(undefined)}
          targetSeqId={editTargetModalConfig.entityId}
        />
      </AngularDiProviders>
    </FullScreenModal>
  );
}

export function EditTargetDialogInner({ targetSeqId, onClose }) {
  const maxWidth = '820px';

  const [loadingPage, setLoadingPage] = useState(false);
  const [pageLoadError, setPageLoadError] = useState();

  const [target, setTarget] = useState<Target>();
  const [targetData, setTargetData] = useState<NodeConfigData>();

  const _targetEditService = useService(TargetEditService);
  const _targetSettingsService = useService(TargetSettingsService);

  const dataIdGenerator = getDataIdGenerator('editTargetDialog');

  useEffect(() => {
    resolveDependencies();
  }, []);

  const hide = () => {
    onClose();
  };

  const resolveDependencies = async () => {

    try {
      setLoadingPage(true);
      setPageLoadError(null);

      const { data: rawTarget } = await ActivateTargetAPI.getTarget(targetSeqId, false);

      if (rawTarget) {
        const targetData = TargetFactory(rawTarget);

        setTarget(targetData);

        _targetSettingsService.getNodeFromExistingData$(
          TARGET_TYPES[targetData.targetType], targetData, true,
        ).subscribe((targetConfig) => {
          setTargetData({ ...targetConfig });
        });
      }

      setLoadingPage(false);
    } catch (err) {
      setLoadingPage(false);
      setPageLoadError(err);
    }
  };

  const getTargetDocumentationLink = (type) => {
    return TARGET_TYPES[type].docLink;
  };

  const canEditTarget = target;

  const canShowSideBySideDocs = false;

  const onSubmit = () => {
    _targetEditService.updateTargetEditSuccessful();
    hide();
  };

  const targetTypeInfo = (): TargetTypeInfo => {
    return {
      type: target.targetType,
      typeDisplay: target.targetTypeDisplay,
    };
  };

  return (
    <div className={`scrollable-content ${canShowSideBySideDocs && !pageLoadError && 'flex-1'}`}>
      <HdButton
        className={styles.hide}
        dataId={dataIdGenerator('go-back')}
        type='button'
        variation='flat'
        color='secondary'
        icon='back'
        onClick={hide}
      >
        Go Back
      </HdButton>

      <div className='body-container mt-4'>
        {loadingPage && <ConnectorFormShimmer />}

        {!loadingPage && pageLoadError ? (
          <RetryApiAlert
            actionHandler={resolveDependencies}
            error={pageLoadError}
            dataId={dataIdGenerator('')}
          />
        ) : null}

        {!loadingPage && !pageLoadError && canEditTarget ? (
          <>
            <div className='header-container'>
              <div className='header'>
                Edit {target.targetTypeDisplay} Target connection settings
              </div>

              {!loadingPage && getTargetDocumentationLink(target.targetType) && (
                <div className='subheader'>
                  To know more about configuring Target, you can read our {' '}

                  <HdDocLink
                    dataId={dataIdGenerator('documentation')}
                    icon='new-window'
                    docLink={getTargetDocumentationLink(target.targetType)}
                  >
                    Documentation
                  </HdDocLink>
                </div>
              )}
            </div>

            <div className='settings-content' style={{ maxWidth }}>
              <TargetSettingsFormWrapper
                targetType={targetTypeInfo()}
                targetData={targetData}
                updateSettings
                submit={onSubmit}
              />
            </div>
          </>
        ) : null}

        {!loadingPage && !pageLoadError && !canEditTarget && (
          <div className='forbidden-error-box height-100'>
            <HdIcon name='password' className='forbidden-error-box-thumbnail' />

            <span className='forbidden-error-box-title'>
              You don't have sufficient permissions
            </span>

            <span className='forbidden-error-box-message'>
              You dont have the privilege to edit this Target.
              Please ask the entity owner to grant access.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
